// /ui/CollapsibleSection.tsx
import React, { useState, useRef, CSSProperties, useCallback, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronRight } from '@/components/ui/lucide-memo';

const TITLE_STYLE: CSSProperties = { fontSize: '0.9rem', lineHeight: 1 };

interface CollapsibleSectionProps {
  icon?: React.ReactNode;
  title: string | React.ReactNode;
  children?: React.ReactNode;
  isVisible?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void; // Optional callback for when visibility changes
  rightContent?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = React.memo(({
  icon,
  title,
  children,
  isVisible = false,
  onVisibilityChange,
  rightContent,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  // Internal state to manage expansion. It's always synced with the isVisible prop.
  const [internalIsExpanded, setInternalIsExpanded] = useState(isVisible);

  // Effect to sync internal state if isVisible prop changes externally
  useEffect(() => {
    setInternalIsExpanded(isVisible);
  }, [isVisible]);

  // currentDisplayState is derived from internalIsExpanded, which is synced from isVisible prop
  const currentDisplayState = internalIsExpanded;

  const toggleVisibility = useCallback(() => {
    const newProposedState = !currentDisplayState;
    if (onVisibilityChange) {
      // If controlled (parent managing visibility via onVisibilityChange),
      // just notify the parent. The parent will update the `isVisible` prop,
      // which will then update `internalIsExpanded` via the useEffect.
      onVisibilityChange(newProposedState);
    } else {
      // If uncontrolled (no onVisibilityChange prop), update internal state directly.
      setInternalIsExpanded(newProposedState);
    }
  }, [onVisibilityChange, currentDisplayState]);

  const handleHeaderClick = useCallback((event: React.MouseEvent) => {
    if (headerRef.current && event.target instanceof Node && headerRef.current.contains(event.target)) {
      const rightContentElement = headerRef.current.querySelector('.right-content-wrapper');
      if (!rightContentElement || !rightContentElement.contains(event.target)) {
        toggleVisibility();
      }
    }
  }, [toggleVisibility]);

  const contentStyle: CSSProperties = useMemo(() => ({
    overflow: 'hidden',
    transition: 'max-height 0.2s ease-out, opacity 0.2s ease-out, margin-top 0.2s ease-out, padding-top 0.2s ease-out, padding-bottom 0.2s ease-out',
    maxHeight: currentDisplayState ? '1000px' : '0px',
    opacity: currentDisplayState ? 1 : 0,
    paddingTop: currentDisplayState ? '4px' : '0px',
    paddingBottom: currentDisplayState ? '4px' : '0px',
    paddingLeft: '4px',
    paddingRight: '4px',
    visibility: currentDisplayState ? 'visible' : 'hidden',
    pointerEvents: currentDisplayState ? 'auto' : 'none',
  }), [currentDisplayState]);

  const headerStyle = useMemo(() => ({
    borderBottom: 'none',
    paddingBottom: '0px',
  }), []);

  return (
    <div className="collapsible-section">
      <div
        className="header flex items-center justify-between cursor-pointer"
        onClick={handleHeaderClick}
        style={headerStyle}
        ref={headerRef}
        role="button"
        aria-expanded={currentDisplayState}
      >
        <div className="flex items-center grow">
          {icon && <div className="mr-2">{icon}</div>}
          <span className="text-white" style={TITLE_STYLE}>
            {title}
          </span>
        </div>
        <div className="flex items-center ml-2">
          {currentDisplayState ? <ChevronDown size={14} color="white" /> : <ChevronRight size={14} color="white" />}
          {rightContent && (
            <div className="right-content-wrapper flex items-center ml-2">
              {rightContent}
            </div>
          )}
        </div>
      </div>

      <div
        className="content"
        style={contentStyle}
        aria-hidden={!currentDisplayState}
      >
        {children}
      </div>
    </div>
  );
});

CollapsibleSection.displayName = 'CollapsibleSection';
export default CollapsibleSection;